/**
 * Moses coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_bible_stories`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mosesColoringPagesPack: ToilePack = {
    slug: 'moses-coloring-pages',
    category: 'faith',
    name: 'Moses coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'basket', label: 'Baby in basket', subject: 'baby Moses in a woven basket floating among the reeds of the Nile, his sister Miriam watching from the bank'},
        {key: 'redsea', label: 'Red Sea', subject: 'Moses holding his staff high as the Red Sea parts into two walls of water, the Israelites walking through on dry ground'},
        {key: 'rock', label: 'Water from rock', subject: 'Moses striking a rock with his staff and water gushing out, thirsty people and sheep drinking around it'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'bold thick-outline coloring book style, one heavy uniform black line, big simple open shapes for young children', palette: 'black ink only', framing: 'The drawing centered with a wide white margin on all sides'},
        {key: 'classic', label: 'Classic', style: 'classic coloring book line art, medium uniform black line, moderate detail and open shapes for ages six to nine', palette: 'black ink only', framing: 'The full page used, the drawing centered with a white margin'},
        {key: 'detailed', label: 'Fine detail', style: 'fine-line detailed coloring page, thin uniform black line, patterned robes, leaves and waves for older children', palette: 'black ink only', framing: 'The scene held inside a plain thin rectangular border with a white margin outside it'},
    ],
    directions: ['the plagues', 'miriam dancing', 'manna'],
    directionHint: 'try: the plagues',
    seo: {
        title: 'Moses Coloring Pages: Basket, Red Sea, Water from Rock',
        metaDescription: 'Nine Moses coloring pages for Sunday school from Exodus: baby Moses on the Nile, the Red Sea and water from the rock, in three line styles.',
        h1: 'Moses coloring pages: the basket on the Nile, the Red Sea, water from the rock',
        tagline: 'Nine pages from the Nile to the wilderness, three scenes of Exodus, each in three line weights.',
        intro: 'Printable Moses coloring pages for Sunday school and Christian homeschool, from the book of Exodus. Three scenes: baby Moses in a woven basket among the reeds of the Nile, Moses lifting his staff as the Red Sea parts, and Moses striking the rock so water gushes out for the people and their sheep. Bold, classic and fine-detail lines. Type a different scene, and the same styles carry it.',
        useCases: [
            'a Sunday school lesson on Exodus',
            "a children's church craft time",
            'a Christian homeschool Bible unit',
            'a classroom reading corner display',
        ],
        faq: [
            {q: "Which parts of Moses' story do these show?", a: 'Three scenes from Exodus: the baby in the basket, the Red Sea, and water from the rock.'},
            {q: 'Does each scene come in all three styles?', a: 'Yes. Each of the three scenes comes in bold, classic and fine-detail lines, nine pages in all.'},
            {q: 'Can I ask for the plagues or manna?', a: 'Yes. Type the scene you want. The request shapes the page, but a result is not promised to match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is black line on white, with open shapes to color in.'},
        ],
    },
}
